import React from 'react';
import { VocabularyWord, Language } from '../../types/Vocabulary';
import './VocabularyMatrix.css';

interface VocabularyCardProps {
  word: VocabularyWord;
  onEdit: (word: VocabularyWord) => void;
  onDelete: (wordId: number) => void;
}

const LANGUAGE_LABELS: Record<Language, string> = {
  chinese: '中文',
  french: 'Français',
  hindi: 'हिन्दी',
  english: 'English'
};

const VocabularyCard: React.FC<VocabularyCardProps> = ({ word, onEdit, onDelete }) => {
  const handleEdit = () => {
    onEdit(word);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this word?')) {
      onDelete(word.id);
    }
  };

  const getTranslationForLanguage = (language: Language): string | undefined => {
    return word.translations?.find(t => t.language === language)?.translation;
  };

  return (
    <div className="vocabulary-card">
      <div className="vocabulary-card-header">
        <div className="vocabulary-card-word-section">
          <span className="vocabulary-card-word">{word.word}</span>
          <span className="vocabulary-card-language-badge">{LANGUAGE_LABELS[word.language]}</span>
        </div>
        <div className="vocabulary-card-actions">
          <button 
            className="vocabulary-card-edit-btn" 
            onClick={handleEdit}
            aria-label="Edit word"
          >
            Edit
          </button>
          <button 
            className="vocabulary-card-delete-btn" 
            onClick={handleDelete}
            aria-label="Delete word"
          >
            Delete
          </button>
        </div>
      </div>

      {word.pronunciation && (
        <div className="vocabulary-card-pronunciation">
          <span className="vocabulary-card-label">Pronunciation:</span>
          <span className="vocabulary-card-pronunciation-text">{word.pronunciation}</span>
        </div>
      )}

      <div className="vocabulary-card-translations">
        <span className="vocabulary-card-label">Translations:</span>
        <div className="vocabulary-card-translations-grid">
          {(['chinese', 'french', 'hindi', 'english'] as Language[]).map((lang) => {
            if (lang === word.language) return null;
            const translation = getTranslationForLanguage(lang);
            if (!translation) return null;
            return (
              <div key={lang} className="vocabulary-card-translation-item">
                <span className="vocabulary-card-translation-lang">{LANGUAGE_LABELS[lang]}:</span>
                <span className="vocabulary-card-translation-text">{translation}</span>
              </div>
            );
          })}
        </div>
        {word.translations && word.translations.length === 0 && (
          <span className="vocabulary-card-no-translations">No translations yet</span>
        )}
      </div>

      {word.example_sentence && (
        <div className="vocabulary-card-example">
          <span className="vocabulary-card-label">Example:</span>
          <span className="vocabulary-card-example-text">{word.example_sentence}</span>
        </div>
      )}

      {word.notes && (
        <div className="vocabulary-card-notes">
          <span className="vocabulary-card-label">Notes:</span>
          <span className="vocabulary-card-notes-text">{word.notes}</span>
        </div>
      )}
    </div>
  );
};

export default VocabularyCard;

